import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthModal } from '../components/AuthModal';
import { HeroDemo } from '../components/HeroDemo';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { JsonLd } from '../components/JsonLd';
import { PageShell } from '../components/core/PageShell';
import { Section } from '../components/core/Section';
import { Heading } from '../components/core/Heading';
import { Text } from '../components/core/Text';
import { Eyebrow } from '../components/core/Eyebrow';
import { TrustStrip } from '../components/core/TrustStrip';
import { trackLandingCtaClicked } from '../lib/analytics';
import { EditorialFlow } from '../components/pretext';
import { buildFontString } from '../lib/pretext';

const FAKE_STEPS = [
  'Evaluating facial symmetry...',
  'Analyzing lighting quality...',
  'Assessing expression warmth...',
  'Scoring background composition...',
];

const FAQ_ITEMS = [
  { q: 'Is my photo private?', a: 'Yes, completely private. Photos are analyzed by AI only, never seen by humans, and automatically deleted after 30 days. They are never used for model training.' },
  { q: 'How accurate is the score?', a: 'Very accurate. Three independent AI models score every photo separately, and the composite averages out individual bias. Calibrated against 47,000+ real profile photos.' },
  { q: 'What does 1 credit get me?', a: 'A complete photo analysis. You get a 3-model consensus score, trait breakdown, specific coaching on what to fix, outfit review, and profile suggestions \u2014 all from one upload.' },
  { q: 'What if the analysis fails?', a: 'Your credit is automatically refunded. Zero risk \u2014 you never pay for a failed analysis.' },
  { q: 'Do credits expire?', a: 'No. Credits never expire. Use them whenever you want.' },
  { q: 'Can I compare multiple photos?', a: 'Yes. Use Compare to rank 2\u201320 photos at once and find your strongest option. Batch pricing starts at 3 credits for up to 5 photos.' },
  { q: 'Is this for men and women?', a: 'Yes, all genders and ages. PhotoFeeler evaluates the photo \u2014 lighting, expression, composition \u2014 not the person.' },
  { q: 'Will the AI judge my appearance?', a: 'No. It rates the photograph \u2014 lighting, angle, expression, composition, and outfit. It never evaluates your body, face shape, age, or physical traits.' },
  { q: 'What\u2019s the difference between Dating and LinkedIn?', a: 'Different scoring criteria. Dating optimizes for attractiveness, warmth, and approachability. LinkedIn optimizes for competence, influence, and professionalism.' },
  { q: 'Can I get a refund on credits?', a: 'Credits are non-refundable, but failed analyses are always refunded automatically. You never lose a credit without getting a result.' },
];

const TRUST_LABELS = [
  'Private',
  'Auto-delete in 30 days',
  '3-model consensus',
  'Refund if analysis fails',
];

const PRICING_TIERS = [
  { name: 'Starter', price: '$5', credits: '5 credits', rate: '$1/each', featured: false },
  { name: 'Standard', price: '$10', credits: '10 credits', rate: '$1/each', featured: false },
  { name: 'Pro', price: '$20', credits: '25 credits', rate: '$0.80/each', featured: true },
];

function FaqAccordion({ items }: { items: typeof FAQ_ITEMS }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = useCallback((index: number) => {
    setOpenIndex(prev => (prev === index ? null : index));
  }, []);

  return (
    <div className="space-y-3">
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div key={i} className="card overflow-hidden">
            <button
              onClick={() => toggle(i)}
              className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer"
              aria-expanded={isOpen}
            >
              <span className="font-display text-base text-ink" style={{ fontWeight: 500 }}>{item.q}</span>
              <svg
                className="shrink-0 w-5 h-5 text-ink-faint transition-transform duration-300"
                style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isOpen && (
              <div className="px-5 pb-5 text-sm text-ink-muted font-ui leading-relaxed animate-fade-in" style={{ fontWeight: 300 }}>
                {item.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function Landing() {
  usePageMetadata({
    title: 'PhotoFeeler AI — Choose Better Dating and LinkedIn Photos',
    description:
      'Choose better dating and LinkedIn profile photos. Upload your options, pick the winner, improve the rest, and track what actually works.',
    canonicalPath: '/',
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authDefaultSignIn, setAuthDefaultSignIn] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showFakeAnalysis, setShowFakeAnalysis] = useState(false);
  const [showBlurredResults, setShowBlurredResults] = useState(false);
  const [fakeStep, setFakeStep] = useState(0);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [pendingVertical, setPendingVertical] = useState<'dating' | 'linkedin' | null>(null);
  const [sampleVertical, setSampleVertical] = useState<'dating' | 'linkedin'>('dating');
  const fileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useScrollReveal();

  // Auto-open auth modal when ?auth=1 or ?auth=signin is in URL
  useEffect(() => {
    const authParam = searchParams.get('auth');
    if (authParam && !isAuthenticated) {
      setAuthDefaultSignIn(authParam === 'signin');
      setAuthOpen(true);
    }
  }, [searchParams, isAuthenticated]);

  // Sticky mobile CTA — show after scrolling past hero
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openAuth = () => {
    if (isAuthenticated) navigate('/app');
    else setAuthOpen(true);
  };

  // Wrap Google sign-in to persist upload intent across the OAuth redirect
  const handleGoogleSignIn = async () => {
    if (pendingFile) {
      sessionStorage.setItem('postAuthRoute', '/upload');
    }
    await signInWithGoogle();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isAuthenticated) {
      navigate('/upload', { state: { file } });
    } else {
      setPendingFile(file);
      setShowFakeAnalysis(true);
      setFakeStep(0);

      // Cycle through fake analysis steps
      const stepInterval = setInterval(() => {
        setFakeStep(prev => {
          if (prev >= FAKE_STEPS.length - 1) {
            clearInterval(stepInterval);
            return prev;
          }
          return prev + 1;
        });
      }, 700);

      // After 3 seconds, show blurred results
      setTimeout(() => {
        clearInterval(stepInterval);
        setShowFakeAnalysis(false);
        setShowBlurredResults(true);
        // After 2 more seconds, open auth modal
        setTimeout(() => {
          setAuthOpen(true);
        }, 2000);
      }, 3000);
    }
  };

  const handleAuthComplete = () => {
    if (pendingFile) {
      navigate('/upload', { state: { file: pendingFile, preselectedVertical: pendingVertical ?? undefined } });
      setPendingFile(null);
      setPendingVertical(null);
    } else if (pendingVertical) {
      navigate('/upload', { state: { preselectedVertical: pendingVertical } });
      setPendingVertical(null);
    } else {
      navigate('/app');
    }
  };

  return (
    <div className="relative overflow-hidden bg-canvas" ref={scrollRef}>
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'Organization',
        'name': 'PhotoFeeler AI',
        'url': 'https://photofeeler.ai',
        'description': 'Premium photo and profile coaching for dating apps and LinkedIn.',
        'logo': 'https://photofeeler.ai/og-image.png',
      }} />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': FAQ_ITEMS.map(item => ({
          '@type': 'Question',
          'name': item.q,
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': item.a,
          },
        })),
      }} />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'PhotoFeeler AI',
        'url': 'https://photofeeler.ai',
        'description': 'Premium photo and profile coaching for dating apps and LinkedIn.',
      }} />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        'name': 'PhotoFeeler AI',
        'applicationCategory': 'LifestyleApplication',
        'operatingSystem': 'Web',
        'offers': {
          '@type': 'Offer',
          'price': '0',
          'priceCurrency': 'USD',
          'description': '3 free credits to start',
        },
        'description': 'AI-powered photo coaching for dating and LinkedIn profiles. Upload a photo, get scored by 3 AI models, and learn exactly what to improve.',
      }} />

      {/* Auth Modal */}
      <AuthModal
        isOpen={authOpen}
        onClose={() => { setAuthOpen(false); setAuthDefaultSignIn(false); setShowBlurredResults(false); setPendingFile(null); }}
        onGoogleSignIn={handleGoogleSignIn}
        onEmailSignIn={signInWithEmail}
        onEmailSignUp={signUpWithEmail}
        customHeading={pendingFile ? 'Your photo is ready' : undefined}
        customSubtext={pendingFile ? 'Sign up to get your AI score — 3 free credits, no card.' : undefined}
        defaultSignIn={authDefaultSignIn}
        onAuthComplete={handleAuthComplete}
      />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />

      {/* Decorative orbs */}
      <div className="page-orb -left-24 top-20 h-[24rem] w-[24rem] bg-accent/15" />
      <div className="page-orb right-[-6rem] top-24 h-[26rem] w-[26rem] bg-info/10" />

      {/* ===================================================================
          1. HERO — product statement, primary CTA, trust strip, proof
          =================================================================== */}
      <section className="pt-32 pb-16 px-5 sm:px-8">
        <PageShell width="default">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
            {/* Left column */}
            <div className="space-y-7 animate-slide-up">
              <Eyebrow>First impression studio</Eyebrow>

              <Heading level={1} variant="display">
                Choose better profile photos. Build a stronger{' '}
                <span className="text-accent">first impression.</span>
              </Heading>

              <Text variant="muted" size="lg" className="max-w-xl">
                Start with Dating or LinkedIn. We help you choose the strongest photo,
                understand why it wins, and improve the rest of your profile.
              </Text>

              {/* 2. Primary + Secondary CTAs */}
              <div className="flex flex-wrap gap-4 pt-2">
                <button onClick={() => {
                  trackLandingCtaClicked('landing', 'dating', 'hero_primary');
                  if (isAuthenticated) {
                    navigate('/dating');
                  } else {
                    setPendingVertical('dating');
                    setAuthOpen(true);
                  }
                }} className="btn-rose text-sm">
                  Improve dating photos
                </button>
                <button onClick={() => {
                  trackLandingCtaClicked('landing', 'linkedin', 'hero_secondary');
                  if (isAuthenticated) {
                    navigate('/linkedin');
                  } else {
                    setPendingVertical('linkedin');
                    setAuthOpen(true);
                  }
                }} className="btn-secondary text-sm">
                  Improve LinkedIn photo
                </button>
              </div>

              {/* 3. Trust strip */}
              <TrustStrip items={TRUST_LABELS} />

              {/* Proof chips */}
              <div className="flex flex-wrap gap-3">
                <div className="hero-chip">Choose the winner</div>
                <div className="hero-chip">Improve copy and outfit</div>
                <div className="hero-chip">Track what actually works</div>
              </div>

              {/* 4. Proof metrics */}
              <div className="grid sm:grid-cols-3 gap-3 pt-2">
                {[
                  ['47K+', 'photos analyzed'],
                  ['15s', 'average turnaround'],
                  ['3', 'models in every score'],
                ].map(([value, label]) => (
                  <div key={label} className="metric-tile">
                    <div className="font-display text-3xl text-ink" style={{ fontWeight: 600 }}>{value}</div>
                    <div className="text-xs text-ink-faint font-ui mt-1">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right column — editorial demo panel */}
            <div className="space-y-4 animate-reveal stagger-3">
              <div className="marketing-panel p-4 sm:p-6">
                <HeroDemo />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="metric-tile">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-ink-faint font-ui mb-2">Dating</div>
                  <div className="text-lg font-ui text-ink" style={{ fontWeight: 600 }}>Pick the best lead photo</div>
                  <Text variant="muted" size="sm" className="mt-2">Rank a full lineup, spot missing shots, and tighten your bio around the right vibe.</Text>
                </div>
                <div className="metric-tile">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-ink-faint font-ui mb-2">LinkedIn</div>
                  <div className="text-lg font-ui text-ink" style={{ fontWeight: 600 }}>Choose the recruiter-ready headshot</div>
                  <Text variant="muted" size="sm" className="mt-2">Pair your best photo with stronger copy, clearer positioning, and polish notes you can actually use.</Text>
                </div>
              </div>
            </div>
          </div>
        </PageShell>
      </section>

      {/* ===================================================================
          5. VERTICAL ENTRY POINTS — Dating and LinkedIn
          =================================================================== */}
      <Section>
        <PageShell width="default">
          <div className="marketing-panel-warm p-6 sm:p-8">
            <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-8 items-start">
              <div>
                <div className="section-kicker !text-left !mb-3">Choose your path</div>
                <Heading level={2} variant="display" className="mb-4 leading-tight">
                  Built around the two profile decisions people actually obsess over.
                </Heading>
                <Text variant="muted" className="max-w-lg">
                  Dating and LinkedIn need different signals, but the product loop is the same:
                  pick the best option, understand the signal, improve the weak parts, and return
                  with better results.
                </Text>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/dating" className="card-hover p-6">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-accent font-ui mb-3">Dating</div>
                  <div className="font-display text-3xl text-ink mb-3" style={{ fontWeight: 600 }}>Win the first swipe</div>
                  <div className="space-y-2 text-sm text-ink-muted font-ui">
                    <div>Best lead photo</div>
                    <div>Lineup and order guidance</div>
                    <div>Bio, prompts, and vibe polish</div>
                  </div>
                </Link>
                <Link to="/linkedin" className="card-hover p-6">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-info font-ui mb-3">LinkedIn</div>
                  <div className="font-display text-3xl text-ink mb-3" style={{ fontWeight: 600 }}>Look credible instantly</div>
                  <div className="space-y-2 text-sm text-ink-muted font-ui">
                    <div>Headshot selection</div>
                    <div>Headline and About tightening</div>
                    <div>Banner and polish guidance</div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </PageShell>
      </Section>

      {/* ===================================================================
          SOCIAL PROOF
          =================================================================== */}
      <Section>
        <PageShell width="narrow">
          <div className="section-kicker">Real scores from real workflows</div>
          <Heading level={2} variant="display" className="text-center mb-5 max-w-3xl mx-auto">
            The product does not stop at a score.
          </Heading>
          <Text variant="muted" className="text-center max-w-2xl mx-auto mb-16">
            Every result is built to answer the useful questions: which photo should I use,
            why does it win, what do I improve next, and what should I retest?
          </Text>
        </PageShell>
        <PageShell width="default">
          <div className="flex gap-4 overflow-x-auto scroll-smooth pb-8 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {SCORE_CARDS.map((card, i) => (
              <div key={i} className="snap-start shrink-0 w-60 card p-4 hover:shadow-card-hover transition-shadow">
                <img src={card.photo} alt={card.context} className="w-full h-40 rounded-[20px] object-cover mb-4" />
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-ui text-ink-muted font-medium">{card.context}</span>
                  <span className={`text-lg font-ui ${card.score >= 80 ? 'text-success' : card.score >= 60 ? 'text-info' : 'text-amber-600'}`} style={{ fontWeight: 700 }}>{card.score}</span>
                </div>
                <div className="text-xs text-ink-muted font-ui leading-relaxed">{card.highlight}</div>
              </div>
            ))}
            <div className="shrink-0 w-4" />
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { quote: 'I tested 5 LinkedIn headshots. The one I thought was worst scored highest \u2014 now it gets 3x more profile views.', name: 'Priya S.', role: 'Product Manager' },
              { quote: 'My match rate on Hinge went from ~2% to 8% after switching to PhotoFeeler\u2019s #1 pick. Four dates in two weeks.', name: 'Jake M.', role: 'Software Engineer' },
              { quote: 'Scored 61, retook with the coaching tips, got 79. Updated my Bumble and got my first Super Like the same week.', name: 'Sarah K.', role: 'Teacher' },
            ].map(t => (
              <div key={t.name} className="card p-5">
                <p className="text-sm text-ink-muted font-ui leading-relaxed mb-3 italic" style={{ fontWeight: 300 }}>&ldquo;{t.quote}&rdquo;</p>
                <div className="text-xs font-ui">
                  <span className="font-medium text-ink">{t.name}</span>
                  <span className="text-ink-faint"> &middot; {t.role}</span>
                </div>
              </div>
            ))}
          </div>
        </PageShell>
      </Section>

      {/* ===================================================================
          6. SAMPLE RESULT PREVIEW
          =================================================================== */}
      <Section id="see-result" className="py-24">
        <PageShell width="narrow">
          <div className="section-kicker">Sample result</div>
          <Heading level={2} variant="display" className="text-center mb-5">
            See what you get after one upload.
          </Heading>
          <Text variant="muted" className="text-center max-w-xl mx-auto mb-10">
            A clear score, a defensible recommendation, and specific coaching you can use for the next retest.
          </Text>
          {/* Vertical toggle */}
          <div className="flex justify-center gap-2 mb-12">
            <button
              onClick={() => setSampleVertical('dating')}
              className={`px-4 py-1.5 rounded-full text-xs font-ui font-medium transition-colors ${
                sampleVertical === 'dating'
                  ? 'bg-accent/10 text-accent border border-accent/20'
                  : 'bg-surface text-ink-faint border border-surface-border hover:text-ink-muted'
              }`}
            >
              Dating sample
            </button>
            <button
              onClick={() => setSampleVertical('linkedin')}
              className={`px-4 py-1.5 rounded-full text-xs font-ui font-medium transition-colors ${
                sampleVertical === 'linkedin'
                  ? 'bg-info/10 text-info border border-info/20'
                  : 'bg-surface text-ink-faint border border-surface-border hover:text-ink-muted'
              }`}
            >
              LinkedIn sample
            </button>
          </div>
        </PageShell>
        <PageShell width="narrow">
          {sampleVertical === 'dating' ? (
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="marketing-panel-warm p-8 text-center">
              <div className="text-[11px] font-ui text-ink-faint tracking-wider uppercase mb-3">Sample Result &middot; Dating</div>
              <div className="text-7xl font-ui text-emerald-600 mb-1" style={{ fontWeight: 600, letterSpacing: '-0.04em' }}>84</div>
              <div className="text-sm font-ui text-ink mb-6" style={{ fontWeight: 500 }}>Strong photo</div>
              <div className="space-y-2 text-left">
                {[['Attractiveness', 8.4], ['Trustworthiness', 8.1], ['Likability', 7.8]].map(([name, val]) => (
                  <div key={name as string} className="flex items-center gap-2">
                    <span className="text-[11px] font-ui text-ink-faint w-28" style={{ fontWeight: 300 }}>{name as string}</span>
                    <div className="flex-1 h-[3px] bg-cream-dark rounded-full overflow-hidden">
                      <div className="h-full bg-blue-400 rounded-full" style={{ width: `${((val as number) / 10) * 100}%` }} />
                    </div>
                    <span className="text-[11px] font-ui text-ink-muted w-6 text-right" style={{ fontWeight: 500 }}>{(val as number).toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="card p-4">
                <div className="text-[11px] uppercase tracking-[0.22em] text-ink-faint font-ui mb-2">Best next action</div>
                <EditorialFlow
                  text="Retake with softer window light. The strongest gains come from changing one thing at a time, then comparing the new version against your current winner."
                  font={buildFontString(400, 14, 'ui')}
                  className="text-ink-muted"
                >
                  <div className="w-14 h-14 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                    <span className="text-xl font-ui text-emerald-600" style={{ fontWeight: 600 }}>84</span>
                  </div>
                </EditorialFlow>
              </div>
              <div>
                <div className="text-xs font-ui font-medium text-emerald-600 tracking-wider uppercase mb-2">What&apos;s working</div>
                <div className="space-y-2">
                  <div className="flex items-start gap-2 text-sm text-ink-muted font-ui" style={{ fontWeight: 300 }}><span className="text-emerald-500 mt-0.5">&#10003;</span> Natural Duchenne smile &mdash; reaches the eyes</div>
                  <div className="flex items-start gap-2 text-sm text-ink-muted font-ui" style={{ fontWeight: 300 }}><span className="text-emerald-500 mt-0.5">&#10003;</span> Good eye contact with the camera</div>
                </div>
              </div>
              <div>
                <div className="text-xs font-ui font-medium text-accent tracking-wider uppercase mb-2">How to improve</div>
                <div className="p-3 rounded-lg border border-accent/20 bg-rose-faint">
                  <div className="text-[10px] font-ui text-accent tracking-wider uppercase mb-1 font-medium">High impact</div>
                  <div className="text-sm font-ui font-medium text-ink mb-1">Harsh overhead lighting</div>
                  <span className="block text-sm text-ink-muted font-ui leading-relaxed" style={{ fontWeight: 300 }}>Face a window &mdash; natural side light scores 10-15 points higher.</span>
                </div>
              </div>
              <button onClick={openAuth} className="btn-rose text-sm mt-4">Get your analysis free &rarr;</button>
            </div>
          </div>
          ) : (
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="marketing-panel-warm p-8 text-center">
              <div className="text-[11px] font-ui text-ink-faint tracking-wider uppercase mb-3">Sample Result &middot; LinkedIn</div>
              <div className="text-7xl font-ui text-emerald-600 mb-1" style={{ fontWeight: 600, letterSpacing: '-0.04em' }}>87</div>
              <div className="text-sm font-ui text-ink mb-6" style={{ fontWeight: 500 }}>Strong photo</div>
              <div className="space-y-2 text-left">
                {[['Competence', 8.6], ['Influence', 7.9], ['Approachability', 8.2]].map(([name, val]) => (
                  <div key={name as string} className="flex items-center gap-2">
                    <span className="text-[11px] font-ui text-ink-faint w-28" style={{ fontWeight: 300 }}>{name as string}</span>
                    <div className="flex-1 h-[3px] bg-cream-dark rounded-full overflow-hidden">
                      <div className="h-full bg-blue-400 rounded-full" style={{ width: `${((val as number) / 10) * 100}%` }} />
                    </div>
                    <span className="text-[11px] font-ui text-ink-muted w-6 text-right" style={{ fontWeight: 500 }}>{(val as number).toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="card p-4">
                <div className="text-[11px] uppercase tracking-[0.22em] text-ink-faint font-ui mb-2">Best next action</div>
                <EditorialFlow
                  text="Add a warmer expression. Clean background and confident posture project authority. Consider a warmer expression to balance approachability."
                  font={buildFontString(400, 14, 'ui')}
                  className="text-ink-muted"
                >
                  <div className="w-14 h-14 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                    <span className="text-xl font-ui text-emerald-600" style={{ fontWeight: 600 }}>87</span>
                  </div>
                </EditorialFlow>
              </div>
              <div>
                <div className="text-xs font-ui font-medium text-emerald-600 tracking-wider uppercase mb-2">What&apos;s working</div>
                <div className="space-y-2">
                  <div className="flex items-start gap-2 text-sm text-ink-muted font-ui" style={{ fontWeight: 300 }}><span className="text-emerald-500 mt-0.5">&#10003;</span> Professional attire reads as intentional</div>
                  <div className="flex items-start gap-2 text-sm text-ink-muted font-ui" style={{ fontWeight: 300 }}><span className="text-emerald-500 mt-0.5">&#10003;</span> Clean background eliminates distraction</div>
                </div>
              </div>
              <div>
                <div className="text-xs font-ui font-medium text-accent tracking-wider uppercase mb-2">How to improve</div>
                <div className="p-3 rounded-lg border border-accent/20 bg-rose-faint">
                  <div className="text-[10px] font-ui text-accent tracking-wider uppercase mb-1 font-medium">High impact</div>
                  <div className="text-sm font-ui font-medium text-ink mb-1">Expression is slightly stiff</div>
                  <span className="block text-sm text-ink-muted font-ui leading-relaxed" style={{ fontWeight: 300 }}>A relaxed half-smile would score 5&ndash;8 points higher on approachability.</span>
                </div>
              </div>
              <button onClick={openAuth} className="btn-rose text-sm mt-4">Get your analysis free &rarr;</button>
            </div>
          </div>
          )}
        </PageShell>
      </Section>

      {/* ===================================================================
          WHY NOT FRIENDS — comparison table
          =================================================================== */}
      <Section>
        <PageShell width="narrow">
          <Heading level={2} variant="display" className="text-center mb-5">
            Why not just ask friends?
          </Heading>
          <Text variant="muted" className="text-center max-w-xl mx-auto mb-12">
            Three very different sources of feedback.
          </Text>
          <div className="marketing-panel-warm p-6 sm:p-8">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-surface-border">
                  <th className="pb-3 font-ui font-medium text-ink-faint text-xs uppercase tracking-wider"></th>
                  <th className="pb-3 font-ui font-medium text-ink-faint text-xs uppercase tracking-wider">Friends</th>
                  <th className="pb-3 font-ui font-medium text-ink-faint text-xs uppercase tracking-wider">Reddit / Strangers</th>
                  <th className="pb-3 font-ui font-medium text-ink-faint text-xs uppercase tracking-wider">PhotoFeeler AI</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-border/50">
                  <td className="py-3 font-ui font-medium text-ink text-xs">Honesty</td>
                  <td className="py-3 text-ink-muted text-xs font-ui">Too nice to be real</td>
                  <td className="py-3 text-ink-muted text-xs font-ui">Harsh and subjective</td>
                  <td className="py-3 text-ink font-medium text-xs font-ui">Honest but kind</td>
                </tr>
                <tr className="border-b border-surface-border/50">
                  <td className="py-3 font-ui font-medium text-ink text-xs">Actionable</td>
                  <td className="py-3 text-ink-muted text-xs font-ui">&ldquo;They all look great!&rdquo;</td>
                  <td className="py-3 text-ink-muted text-xs font-ui">Opinions, not advice</td>
                  <td className="py-3 text-ink font-medium text-xs font-ui">Score + what to fix + how</td>
                </tr>
                <tr className="border-b border-surface-border/50">
                  <td className="py-3 font-ui font-medium text-ink text-xs">Speed</td>
                  <td className="py-3 text-ink-muted text-xs font-ui">Days to gather opinions</td>
                  <td className="py-3 text-ink-muted text-xs font-ui">Hours for a thread</td>
                  <td className="py-3 text-ink font-medium text-xs font-ui">15 seconds</td>
                </tr>
                <tr>
                  <td className="py-3 font-ui font-medium text-ink text-xs">Privacy</td>
                  <td className="py-3 text-ink-muted text-xs font-ui">Friends see your photo</td>
                  <td className="py-3 text-ink-muted text-xs font-ui">Strangers see your photo</td>
                  <td className="py-3 text-ink font-medium text-xs font-ui">Private, auto-deleted in 30 days</td>
                </tr>
              </tbody>
            </table>
          </div>
        </PageShell>
      </Section>

      {/* ===================================================================
          FAQ / TRUST REINFORCEMENT
          =================================================================== */}
      <Section>
        <PageShell width="narrow">
          <div className="section-kicker">Common questions</div>
          <Heading level={2} variant="display" className="text-center mb-5 max-w-2xl mx-auto">
            Everything you need to know before your first upload.
          </Heading>
          <FaqAccordion items={FAQ_ITEMS} />
        </PageShell>
      </Section>

      {/* ===================================================================
          7. PRICING TEASER
          =================================================================== */}
      <Section className="py-24">
        <PageShell width="narrow">
          <div className="text-center">
            <div className="section-kicker">Pricing</div>
            <Heading level={2} variant="display" className="text-center mb-5">
              Simple pricing, built for repeat testing.
            </Heading>
            <Text variant="muted" className="text-center max-w-xl mx-auto mb-16">
              Use the free credits to get the first winner. Upgrade when you want deeper compare,
              rewrites, retests, and profile progress.
            </Text>

            <div className="grid sm:grid-cols-3 gap-5 max-w-2xl mx-auto">
              {PRICING_TIERS.map(tier => (
                <div key={tier.name} className={`${tier.featured ? 'card-rose' : 'card'} p-6 text-center relative`}>
                  {tier.featured && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-ui font-medium text-accent bg-rose-faint px-3 py-0.5 rounded-full border border-accent/15">Best value</div>
                  )}
                  <div className={`text-xs font-ui tracking-wider uppercase mb-3 ${tier.featured ? 'text-accent mt-1' : 'text-ink-faint'}`}>{tier.name}</div>
                  <div className="text-3xl font-ui text-ink" style={{ fontWeight: 600 }}>{tier.price}</div>
                  <div className={`text-xs mt-1 mb-4 ${tier.featured ? 'text-accent font-medium' : 'text-ink-faint'}`}>{tier.credits} &middot; {tier.rate}</div>
                  <button onClick={openAuth} className={`${tier.featured ? 'btn-rose' : 'btn-primary'} text-xs w-full`}>Get {tier.name}</button>
                </div>
              ))}
            </div>

            <button onClick={openAuth} className="btn-rose mt-10 text-sm">
              Start free &mdash; 3 credits included
            </button>
          </div>
        </PageShell>
      </Section>

      {/* Sticky mobile CTA — appears after scrolling past hero */}
      {showStickyCTA && !isAuthenticated && (
        <div data-testid="sticky-cta" className="fixed bottom-0 left-0 right-0 z-40 p-3 bg-canvas/95 backdrop-blur-xl border-t border-surface-border sm:hidden animate-slide-up">
          <Link to="/dating" className="btn-rose text-sm w-full block text-center">
            Improve dating photos
          </Link>
        </div>
      )}

      {/* Fake analysis overlay (lazy registration illusion) */}
      {showFakeAnalysis && (
        <div className="fixed inset-0 z-[90] bg-canvas/95 backdrop-blur-sm flex items-center justify-center animate-fade-in">
          <div className="text-center max-w-xs">
            <div className="w-10 h-10 border-2 border-ink/10 border-t-accent rounded-full animate-spin mx-auto mb-6" />
            <Heading level={2} className="mb-4">Analyzing your photo...</Heading>
            <Text variant="muted" size="sm">{FAKE_STEPS[fakeStep]}</Text>
          </div>
        </div>
      )}

      {/* Blurred fake results overlay (lazy registration illusion) */}
      {showBlurredResults && (
        <div className="fixed inset-0 z-[90] bg-canvas/95 backdrop-blur-sm flex items-center justify-center animate-fade-in">
          <div className="text-center max-w-sm">
            {/* Blurred fake score */}
            <div className="blur-md mb-4">
              <div className="text-6xl font-ui text-success" style={{ fontWeight: 700 }}>78</div>
              <div className="text-sm text-ink-muted font-ui">Good photo &mdash; Top 35%</div>
            </div>
            <div className="blur-md mb-6">
              <div className="text-xs text-ink-muted font-ui">Attractiveness 7.8 &middot; Trustworthiness 8.1 &middot; Likability 7.2</div>
            </div>
            {/* Clear CTA */}
            <Heading level={3} className="mb-2">Your results are ready!</Heading>
            <Text variant="muted" size="sm" className="mb-4">Sign up to see your score and coaching tips.</Text>
            <button onClick={() => { setShowBlurredResults(false); setAuthOpen(true); }} className="btn-rose text-sm">
              See my results &mdash; it&apos;s free
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// === Score cards data for horizontal scroll section ===

const SCORE_CARDS = [
  {
    photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=400&fit=crop&crop=face&q=80',
    context: 'Dating',
    score: 84,
    highlight: 'Great eye contact and warm, genuine smile',
  },
  {
    photo: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=300&h=400&fit=crop&crop=face&q=80',
    context: 'LinkedIn',
    score: 87,
    highlight: 'Clean background, confident professional look',
  },
  {
    photo: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=300&h=400&fit=crop&crop=face&q=80',
    context: 'Dating',
    score: 76,
    highlight: 'Natural light works well, try a slight smile',
  },
  {
    photo: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=300&h=400&fit=crop&crop=face&q=80',
    context: 'LinkedIn',
    score: 79,
    highlight: 'Professional attire, strong posture',
  },
  {
    photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&h=400&fit=crop&crop=face&q=80',
    context: 'Dating',
    score: 89,
    highlight: 'Window light + relaxed, approachable expression',
  },
  {
    photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=300&h=400&fit=crop&crop=face&q=80',
    context: 'LinkedIn',
    score: 85,
    highlight: 'Warm smile projects approachability',
  },
];
